import { TabsLayout } from "@jugl-web/ui-components";
import { useTranslations } from "@jugl-web/utils";
import React, { useState } from "react";
import { useSessionStorage } from "@jugl-web/utils/hooks/useStorage";
import { useEffectOnce } from "react-use";
import { SelectedPlanInfo } from "@web-src/modules/cx/components/CompletePurchaseDialog/components/SummaryStep/types";
import { ANONYMOUS_SESSION_PLAN_INFO } from "@jugl-web/utils/storage";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import { ManageAccountsDrawer } from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer";
import { useManageAccountsDrawer } from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer/providers/ManageAccountsDrawerContext";
import { ManageAccountsDrawerProvider } from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer/providers/ManageAccountsDrawerProvider";
import {
  InstagramAccountProvider,
  WhatsappAccountProvider,
} from "@web-src/modules/client-support/pages/ClientSupportPage/providers";
import { WebChatBotAccountProvider } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/WebChatBotAccountProvider";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntityProvider";
import { useUserRole } from "@web-src/modules/common/hooks/useUserRole";
import { Button } from "@jugl-web/ui-kit";
import { useSubscriptionV2 } from "../../providers/SubscriptionV2Provider";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { ReactComponent as PricingIcon } from "./assets/pricing.svg";
import { useCXSignupDialog } from "../../components/CXSignupDialog/useCXSignupDialog";
import { SetupAndTestTabContent } from "./components/SetupAndTestTabContent";
import { KnowledgeTabContent } from "./components/KnowledgeTabContent";
import { PreviewAndInstallTabContent } from "./components/PreviewAndInstallTabContent";

type TabId = "knowledge" | "setupAndTest" | "previewAndInstall";

const CXAIAgentsPageContent: React.FC = () => {
  const { t } = useTranslations();
  const { entity } = useEntityProvider();
  const { isAnonymous, entityId: anonymousEntityId } = useAnonymousSession();
  const cxSignupDialog = useCXSignupDialog();
  const { isAdmin } = useUserRole();
  const { openSubscriptionDrawer, openPurchaseDialog, isFree } =
    useSubscriptionV2();
  const entityId = entity?.id || anonymousEntityId;
  const [selectedTab, setSelectedTab] = useState<TabId>("knowledge");
  const [selectedPlan, setSelectedPlan] = useSessionStorage<
    SelectedPlanInfo | undefined
  >(ANONYMOUS_SESSION_PLAN_INFO, undefined);

  useEffectOnce(() => {
    if (selectedPlan && !isAnonymous) {
      openPurchaseDialog(selectedPlan);
    }
  });

  const { isManageAccountDrawerOpen, openManageAccountDrawer } =
    useManageAccountsDrawer();

  return (
    <>
      <TabsLayout
        breadcrumbProps={{
          items: [
            {
              title: t({
                id: "cx-ai-agents-page.ai-agent",
                defaultMessage: "AI Agent",
              }),
            },
          ],
        }}
        selectedTab={selectedTab}
        tabs={[
          {
            id: "knowledge",
            title: t({
              id: "cx-ai-agents-page.add-knowledge",
              defaultMessage: "Add Knowledge",
            }),
            number: 1,
            onClick: () => setSelectedTab("knowledge"),
          },
          {
            id: "setupAndTest",
            title: t({
              id: "cx-ai-agents-page.setup-and-test",
              defaultMessage: "Setup & Test",
            }),
            number: 2,
            onClick: () => setSelectedTab("setupAndTest"),
          },
          {
            id: "previewAndInstall",
            title: t({
              id: "cx-ai-agents-page.preview-and-install",
              defaultMessage: "Preview & Install",
            }),
            number: 3,
            onClick: () => setSelectedTab("previewAndInstall"),
          },
        ]}
        rightContent={
          (isAnonymous || (isAdmin && isFree)) && (
            <div className="flex items-center gap-4">
              <Button
                onClick={() =>
                  openSubscriptionDrawer({
                    onSelectPlan: (planInfo) => {
                      if (isAnonymous) {
                        setSelectedPlan(planInfo);
                        cxSignupDialog?.open();
                        return;
                      }
                      openPurchaseDialog(planInfo);
                    },
                  })
                }
                variant="secondary"
                iconEnd={<PricingIcon />}
              >
                {t({
                  id: "cx-ai-agents-page.see-plans",
                  defaultMessage: "See plans",
                })}
              </Button>
              {isAnonymous && (
                <Button
                  onClick={() => cxSignupDialog?.open()}
                  className="w-[200px]"
                  iconEnd={<ArrowIcon />}
                >
                  {t({
                    id: "cx-ai-agents-page.sign-up-for-free",
                    defaultMessage: "Sign Up for free",
                  })}
                </Button>
              )}
            </div>
          )
        }
      >
        {selectedTab === "knowledge" ? (
          <KnowledgeTabContent entityId={entityId} />
        ) : selectedTab === "setupAndTest" ? (
          <SetupAndTestTabContent entityId={entityId} />
        ) : selectedTab === "previewAndInstall" ? (
          <PreviewAndInstallTabContent entityId={entityId} />
        ) : null}
      </TabsLayout>
      <ManageAccountsDrawer
        isOpen={!!isManageAccountDrawerOpen}
        onRequestClose={() => openManageAccountDrawer(undefined)}
        initialScreen={isManageAccountDrawerOpen}
      />
    </>
  );
};

export const CXAIAgentsPage: React.FC = () => {
  return (
    <ManageAccountsDrawerProvider>
      <WhatsappAccountProvider>
        <InstagramAccountProvider>
          <WebChatBotAccountProvider>
            <CXAIAgentsPageContent />
          </WebChatBotAccountProvider>
        </InstagramAccountProvider>
      </WhatsappAccountProvider>
    </ManageAccountsDrawerProvider>
  );
};

export default CXAIAgentsPage;
